import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { TopicsService } from "../../services/topics";
import { Topic, TopicSearchParams } from "../../types/topic";
import { RootState } from "../";
import axios from "axios";
import { CONTENT_SORT_VALUES } from "../../constants/content";

interface TopicsState {
  items: Topic[];
  selectedTopic: Topic | null;
  loading: boolean;
  error: string | null;
  searchQuery: string;
  total: number;
}

const initialState: TopicsState = {
  items: [],
  selectedTopic: null,
  loading: false,
  error: null,
  searchQuery: '',
  total: 0,
};

export const fetchAllTopics = createAsyncThunk(
  "topics/fetchAllTopics",
  async (_, { rejectWithValue }) => {
    try {
      const service = TopicsService.getInstance();
      const topics = await service.getAllTopics();
      return { items: topics, total: topics.length };
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch all topics');
    }
  }
);

export const fetchTopics = createAsyncThunk(
  "topics/fetchTopics",
  async (params: TopicSearchParams, { rejectWithValue }) => {
    try {
      const service = TopicsService.getInstance();
      const response = await service.searchTopics(params);
      return { items: response.results, total: response.total };
    } catch (error: any) {
      if (axios.isCancel(error)) {
        return rejectWithValue({ isCanceled: true });
      }
      return rejectWithValue(error.message || 'Failed to fetch topics');
    }
  }
);

export const fetchTopicById = createAsyncThunk(
  "topics/fetchTopicById",
  async (id: string, { rejectWithValue }) => {
    try {
      const service = TopicsService.getInstance();
      return await service.getTopicById(id);
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch topic');
    }
  }
);

export const fetchTopicsByParent = createAsyncThunk(
  "topics/fetchTopicsByParent",
  async ({ parentId, page, limit }: { parentId: string; page?: number; limit?: number }, { rejectWithValue }) => {
    try {
      const service = TopicsService.getInstance();
      const response = await service.getTopicsByParent(parentId, page, limit);
      return { items: response.results, total: response.total };
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch child topics');
    }
  }
);

const topicsSlice = createSlice({
  name: "topics",
  initialState,
  reducers: {
    setSearchQuery: (state, action: PayloadAction<string>) => {
      state.searchQuery = action.payload;
    },
    clearTopics: (state) => {
      state.items = [];
      state.total = 0;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAllTopics.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAllTopics.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload.items;
        state.total = action.payload.total;
      })
      .addCase(fetchAllTopics.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchTopics.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTopics.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload.items;
        state.total = action.payload.total;
      })
      .addCase(fetchTopics.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchTopicById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTopicById.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedTopic = action.payload;
      })
      .addCase(fetchTopicById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchTopicsByParent.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTopicsByParent.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload.items;
        state.total = action.payload.total;
      })
      .addCase(fetchTopicsByParent.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { setSearchQuery, clearTopics } = topicsSlice.actions;

// Selectors
export const selectTopicsState = (state: RootState) => state.topics;
export const selectAllTopics = (state: RootState) => state.topics.items;
export const selectSelectedTopic = (state: RootState) => state.topics.selectedTopic;
export const selectTopicsLoading = (state: RootState) => state.topics.loading;
export const selectTopicsError = (state: RootState) => state.topics.error;
export const selectTopicsTotal = (state: RootState) => state.topics.total;

export default topicsSlice.reducer; 